import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/** Maps a Buy X Get Y discount code to the free-gift product variant it unlocks. */
const GIFT_CODE_VARIANTS = {
  WELCOMEGIFT: 58280744419672,
  GRAZIE500: 58298403291480,
};

/**
 * Minimum spend (in cents, gift excluded) required by a gift code. The discounts themselves
 * have no minimum in Shopify, so that they don't block quantity discounts on the other items.
 */
const GIFT_CODE_MINIMUMS = {
  WELCOMEGIFT: 3000,
};

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    // Mobile keyboards often append a trailing space, which would break the code comparisons below.
    const discountCodeValue = discountCode.value.trim();
    const giftVariantId = GIFT_CODE_VARIANTS[discountCodeValue.toUpperCase()];

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      // Buy X Get Y discounts only report as "applicable" once the gift item is already
      // in the cart, so it must be added before we ask Shopify to evaluate the code.
      const giftMinimum = GIFT_CODE_MINIMUMS[discountCodeValue.toUpperCase()];
      if (giftMinimum && !(await this.#meetsMinimum(giftMinimum))) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      let giftWasAdded = false;
      if (giftVariantId) {
        giftWasAdded = await this.#ensureGiftInCart(giftVariantId);
      }

      const updatedDiscounts = [...existingDiscounts, discountCodeValue];

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: updatedDiscounts.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code.toUpperCase() === discountCodeValue.toUpperCase() && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        if (giftWasAdded && giftVariantId) {
          await this.#removeGiftFromCart(giftVariantId);
        }
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const removedGiftVariantId = GIFT_CODE_VARIANTS[discountCode.trim().toUpperCase()];
    if (removedGiftVariantId) {
      await this.#removeGiftFromCart(removedGiftVariantId);
    }

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Checks whether the cart, gift products excluded, reaches the given minimum spend.
   * @param {number} minimum - The minimum spend in cents.
   * @returns {Promise<boolean>}
   */
  async #meetsMinimum(minimum) {
    const cartResponse = await fetch(`/cart.js?t=${Date.now()}`, { cache: 'no-store' });
    const cart = await cartResponse.json();
    const giftVariantIds = Object.values(GIFT_CODE_VARIANTS);
    const subtotal = cart.items
      .filter(
        (/** @type {{ variant_id: number, final_line_price: number }} */ item) =>
          !giftVariantIds.includes(item.variant_id) && item.final_line_price > 0
      )
      .reduce((/** @type {number} */ total, /** @type {{ original_line_price: number }} */ item) => total + item.original_line_price, 0);
    return subtotal >= minimum;
  }

  /**
   * Adds the given gift product to the cart if it isn't already there.
   * @param {number} variantId - The gift product's variant id.
   * @returns {Promise<boolean>} Whether a new gift line item was added.
   */
  async #ensureGiftInCart(variantId) {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const alreadyInCart = cart.items.some(
        (/** @type {{ variant_id: number }} */ item) => item.variant_id === variantId
      );
      if (alreadyInCart) return false;

      const addResponse = await fetch(
        '/cart/add.js',
        fetchConfig('json', {
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        })
      );

      return addResponse.ok;
    } catch (error) {
      // If this fails, the discount is still applied normally; the gift simply won't be pre-added.
      return false;
    }
  }

  /**
   * Removes the given gift product from the cart, if present.
   * @param {number} variantId - The gift product's variant id.
   */
  async #removeGiftFromCart(variantId) {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const giftItem = cart.items.find(
        (/** @type {{ variant_id: number }} */ item) => item.variant_id === variantId
      );
      if (!giftItem) return;

      await fetch(
        '/cart/change.js',
        fetchConfig('json', {
          body: JSON.stringify({ id: giftItem.key, quantity: 0 }),
        })
      );
    } catch (error) {
      // Non-critical: worst case the customer keeps a full-price gift item in cart.
    }
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
